import api from './authService';
import { config } from '../config';

export const apiService = {
  // Brands
  getBrands: async () => {
    try {
      console.log('Fetching brands from:', '/brands');
      const response = await api.get('/brands');
      console.log('Brands response:', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  },

  // Get brands that have offers for a specific category/subcategory
  getBrandsWithOffers: async (categoryId, subcategoryId, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.includeExpired === 'true' || options.includeExpired === true) {
        params.append('includeExpired', 'true');
      }
      const queryString = params.toString();
      const url = `/brands/with-offers/${categoryId}/${subcategoryId}${queryString ? '?' + queryString : ''}`;
      console.log('Fetching brands with offers from:', url);
      const response = await api.get(url);
      console.log('Brands with offers response:', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching brands with offers:', error);
      throw error;
    }
  },

  // Categories
  getCategories: async () => {
    try {
      console.log('Fetching categories from:', '/categories');
      const response = await api.get('/categories');
      console.log('Categories response:', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Offers
  getOffers: async (params = {}) => {
    const response = await api.get('/offers', { params });
    return response.data;
  },

  getOffersByCategory: async (categoryId, subcategoryId, params = {}) => {
    const response = await api.get(`/offers/by-subcategory/${categoryId}/${subcategoryId}`, { params });
    return response.data;
  },

  getOffersByBrand: async (brandId, params = {}) => {
    const response = await api.get(`/offers/by-brand/${brandId}`, { params });
    return response.data;
  },

  getSavedOfferElementsByBrand: async (brandId) => {
    const response = await api.get(`/offer-elements/brand/${brandId}`);
    return response.data;
  },

  getSavedOfferElementsByOffer: async (brandId, offerId) => {
    const response = await api.get(`/offer-elements/brand/${brandId}/offer/${offerId}`);
    return response.data;
  },

  getOfferById: async (offerId) => {
    const response = await api.get(`/offers/id/${offerId}`);
    return response.data;
  },

  // Affiliate tracking
  getAffiliateStats: async (affiliateId, params = {}) => {
    const response = await api.get(`/affiliate/stats/${affiliateId}`, { params });
    return response.data;
  },

  // Generate affiliate link
  generateAffiliateLink: (offerId, affiliateId, baseUrl = window.location.origin) => {
    // Generate a unique click ID for tracking using crypto API
    const clickId = crypto.randomUUID ? crypto.randomUUID() : 
      Date.now().toString(36) + Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
    
    // Use config for server URL (will use production URL in production, localhost in development)
    const serverUrl = config.SERVER_URL || 'http://localhost:8080';
    
    return `${serverUrl}/api/affiliate/redirect/${offerId}?aff_id=${affiliateId}&click_id=${clickId}`;
  },

  // Generate embed code
  generateEmbedCode: (offer, affiliateId) => {
    const affiliateLink = apiService.generateAffiliateLink(offer._id, affiliateId);
    
    return `<!-- Affiliate Offer: ${offer.title} -->
<div style="border: 1px solid #ddd; border-radius: 8px; padding: 20px; margin: 20px 0; font-family: Arial, sans-serif; max-width: 400px;">
  <h3 style="margin: 0 0 10px 0; color: #333;">${offer.title}</h3>
  <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">${offer.description}</p>
  ${offer.discountPercentage ? `<div style="background: #ff4757; color: white; padding: 5px 10px; border-radius: 4px; display: inline-block; margin-bottom: 10px; font-weight: bold;">${offer.discountPercentage}% OFF</div>` : ''}
  <div style="margin: 15px 0;">
    <a href="${affiliateLink}" 
       target="_blank" 
       style="background: #2630f3; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; font-weight: bold;"
       onclick="window.open(this.href); return false;">
      ${offer.callToAction || 'Get This Deal'}
    </a>
  </div>
  ${offer.urgencyText ? `<p style="margin: 10px 0 0 0; font-size: 12px; color: #ff4757; font-style: italic;">${offer.urgencyText}</p>` : ''}
</div>`;
  },

  // Blog operations
  createBlogPost: async (blogData) => {
    const response = await api.post('/blog', blogData);
    return response.data;
  },

  // Pre-built merged blog operations
  createPreBuiltMergedBlog: async (blogData) => {
    const response = await api.post('/blog/prebuilt', blogData);
    return response.data;
  },

  getPreBuiltMergedBlogBySubcategory: async (categoryId, subcategoryId) => {
    const response = await api.get(`/blog/prebuilt/by-subcategory/${categoryId}/${subcategoryId}`);
    return response.data;
  },

  getAllPreBuiltMergedBlogs: async (categoryId = null) => {
    const params = categoryId ? { categoryId } : {};
    const response = await api.get('/blog/prebuilt', { params });
    return response.data;
  },

  getBlogPosts: async (params = {}) => {
    const response = await api.get('/blog', { params });
    return response.data;
  },

  getMyBlogPosts: async (params = {}) => {
    const response = await api.get('/blog/user/me', { params });
    return response.data;
  },

  getBlogPostById: async (postId) => {
    const response = await api.get(`/blog/id/${postId}`);
    return response.data;
  },

  updateBlogPost: async (postId, blogData) => {
    const response = await api.put(`/blog/${postId}`, blogData);
    return response.data;
  },

  deleteBlogPost: async (postId) => {
    const response = await api.delete(`/blog/${postId}`);
    return response.data;
  },

  getPublicBlogPost: async (slug) => {
    try {
      console.log('📡 Fetching blog post with slug:', slug);
      console.log('📡 API base URL:', api.defaults.baseURL);
      
      // Add cache-busting via timestamp query parameter directly in URL
      // Query parameter is enough for cache-busting, no need for headers that cause CORS issues
      const timestamp = Date.now();
      const encodedSlug = encodeURIComponent(slug);
      const url = `/blog/public/${encodedSlug}?_t=${timestamp}`;
      
      console.log('📡 Full URL:', api.defaults.baseURL + url);
      
      // Don't add custom headers that might cause CORS issues
      // The query parameter _t is sufficient for cache-busting
      const response = await api.get(url);
      
      console.log('✅ Blog post fetched successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching blog post:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: error.config?.baseURL + error.config?.url
      });
      throw error;
    }
  },

  getBlogsBySubcategory: async (categoryId, subcategoryId, params = {}) => {
    const response = await api.get(`/blog/by-subcategory/${categoryId}/${subcategoryId}`, { params });
    return response.data;
  },

  // Comment operations
  addComment: async (postId, commentText) => {
    const response = await api.post(`/blog/${postId}/comments`, { text: commentText });
    return response.data;
  },

  getComments: async (postId) => {
    const response = await api.get(`/blog/id/${postId}`);
    return response.data.comments || [];
  },

  // Reply operations
  addReply: async (postId, commentId, replyText) => {
    const response = await api.post(`/blog/${postId}/comments/${commentId}/reply`, { text: replyText });
    return response.data;
  },

  // Like/Dislike operations
  likeComment: async (postId, commentId) => {
    const response = await api.post(`/blog/${postId}/comments/${commentId}/like`);
    return response.data;
  },

  dislikeComment: async (postId, commentId) => {
    const response = await api.post(`/blog/${postId}/comments/${commentId}/dislike`);
    return response.data;
  },

  removeReaction: async (postId, commentId) => {
    const response = await api.delete(`/blog/${postId}/comments/${commentId}/reaction`);
    return response.data;
  },

  // Test upload configuration
  testUploadConfig: async () => {
    const response = await api.get('/blog/upload/test');
    return response.data;
  },

  // Get categories for select (simplified response)
  getCategoriesForSelect: async () => {
    try {
      const response = await api.get('/categories/select');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories for select:', error);
      throw error;
    }
  },

  // Authentication operations
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Blog page view tracking
  trackBlogPageView: async (blogPostId, affiliateId, offerId, sessionId, pageNumber = 0) => {
    try {
      console.log('📡 [API] trackBlogPageView called:');
      console.log('  📝 Blog Post ID:', blogPostId);
      console.log('  👤 Affiliate ID:', affiliateId);
      console.log('  🎯 Offer ID:', offerId || 'N/A');
      console.log('  🆔 Session ID:', sessionId);
      console.log('  📄 Page Number:', pageNumber);
      console.log('  🌐 API Base URL:', api.defaults.baseURL);
      console.log('  🔗 Full URL:', `${api.defaults.baseURL}/affiliate/blog-view`);
      
      const requestData = {
        blogPostId,
        affiliateId,
        offerId,
        sessionId,
        pageNumber
      };
      
      console.log('  📦 Request Payload:', JSON.stringify(requestData, null, 2));
      
      const response = await api.post('/affiliate/blog-view', requestData);
      
      console.log('✅ [API] trackBlogPageView response:', response.status, response.statusText);
      console.log('  📦 Response Data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ [API] trackBlogPageView error:');
      console.error('  Error Message:', error.message);
      console.error('  Error Response:', error.response?.data);
      console.error('  Error Status:', error.response?.status);
      console.error('  Error Headers:', error.response?.headers);
      console.error('  Request Config:', {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        data: error.config?.data
      });
      throw error;
    }
  },

  // Get last visited page for a blog post by IP
  getBlogLastPage: async (blogPostId) => {
    try {
      const response = await api.get(`/affiliate/blog-last-page/${blogPostId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting last visited page:', error);
      // Return default if error (don't fail)
      return { success: true, lastPageNumber: 0, lastVisitedAt: null };
    }
  },

  // Get blog page view statistics
  getBlogPageViewStats: async (affiliateId, params = {}) => {
    const response = await api.get(`/affiliate/blog-views/stats/${affiliateId}`, { params });
    return response.data;
  },

  // Get blog performance statistics with page-level breakdown
  getBlogPerformanceStats: async (affiliateId, params = {}) => {
    const response = await api.get(`/affiliate/blog-performance/${affiliateId}`, { params });
    return response.data;
  },

  getSnakeStats: async (affiliateId, params = {}) => {
    const response = await api.get(`/affiliate/snake-stats/${affiliateId}`, { params });
    return response.data;
  },

  // Get paginated pages from a prebuilt merged blog (snake pagination)
  getSnakePages: async (prebuiltBlogId, start = 0, limit = 10, affiliateId = null, subcategoryId = null) => {
    try {
      const params = { start, limit };
      // Include affiliateId and subcategoryId for randomization when no performance data
      if (affiliateId) params.affiliateId = affiliateId;
      if (subcategoryId) params.subcategoryId = subcategoryId;
      
      const response = await api.get(`/blog/prebuilt/${prebuiltBlogId}/pages`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching snake pages:', error);
      throw error;
    }
  },

  // User settings operations
  getAffiliateProfile: async () => {
    const response = await api.get('/affiliates/profile');
    return response.data;
  },

  updateAffiliateProfile: async (profileData) => {
    const response = await api.patch('/affiliates/profile', profileData);
    return response.data;
  },

  uploadProfilePicture: async (file) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    const response = await api.post('/affiliates/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get all affiliates (admin only)
  getAllAffiliates: async () => {
    try {
      const response = await api.get('/affiliates');
      return response.data;
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      throw error;
    }
  }
};
